/**
 * spaceEntitySpriteCache.ts
 * Per-id lazy decode cache for the World 2 entity sprite stream
 * (asteroids, drones, mines — any entity whose draw-list rect carries
 * a `spriteId`).
 *
 * Extracted from `webSpaceDraw.ts` so that file stays under the
 * AGENTS.md 300-line cap. The cache is a module-singleton — every
 * draw call shares the same slot map and the same lazy-load latches.
 *
 * @module spaceEntitySpriteCache
 */

/// <reference lib="dom" />

import {
  SPACE_ENTITY_SPRITE_MODULES,
  type SpaceEntitySpriteId,
} from './spaceSpriteManifest';
import {
  type ImageSlot,
  createImageSlot,
  ensureImageLoaded,
} from './lazyImageLoader';

// DECISION: Per-id slot map, lazily populated on first lookup so an
// idle test scene doesn't kick off decodes for every entity sprite.
const entitySlots = new Map<SpaceEntitySpriteId, ImageSlot>();

/**
 * Resolve (or create-and-resolve) the decoded sprite for a given
 * spriteId. Returns `null` while the decode is still in flight or for
 * builds without the asset — caller falls back to the colour rect.
 *
 * @param id - One of the {@link SpaceEntitySpriteId} ids that the
 *   draw-list builders emit.
 * @returns Decoded image once `lazyImageLoader.web` resolves it, else `null`.
 */
export function ensureEntitySprite(
  id: SpaceEntitySpriteId,
): globalThis.HTMLImageElement | null {
  let slot = entitySlots.get(id);
  if (slot === undefined) {
    slot = createImageSlot();
    entitySlots.set(id, slot);
  }
  ensureImageLoaded(slot, SPACE_ENTITY_SPRITE_MODULES[id]);
  return slot.current;
}
